import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { clicksAPI } from '../utils/api';

const REFRESH_MS = 60000; // 1 min

const SIGNAL_LABELS = {
  vpn_detected: 'VPN',
  tor_detected: 'Tor',
  proxy_detected: 'Proxy',
  datacenter_ip: 'Datacenter',
  excessive_clicks_10min: 'Over threshold (10min)',
  excessive_hourly_clicks: 'Over threshold (1hr)',
  excessive_daily_clicks: 'Over threshold (24hr)',
  excessive_weekly_clicks: 'Over threshold (7d)',
  excessive_monthly_clicks: 'Over threshold (30d)',
  excessive_clicks_rule1: 'Over threshold (rule 1)',
  excessive_clicks_rule2: 'Over threshold (rule 2)',
  excessive_clicks_rule3: 'Over threshold (rule 3)',
  suspicious_user_agent: 'Bot UA',
  crawler_detected: 'Crawler',
  suspicious_ip_range: 'Bad IP range',
  ip_range_abuse: 'IP range abuse',
  no_referrer: 'No referrer',
  previously_blocked: 'Previously blocked',
  short_time_on_site: 'Bounced',
  bot_like_behavior: 'Bot behaviour',
  device_id_multiple_ips: 'Device multi-IP',
  device_id_excessive_clicks: 'Device excessive',
  cross_domain_blocked: 'Cross-campaign block',
  country_blocked: 'Country blocked',
  country_not_allowed: 'Country not allowed',
  manually_excluded: 'Manually excluded',
  manual_block: 'Manual',
};

export default function ClickLog({ campaignId }) {
  const [clicks, setClicks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    if (!campaignId) { setClicks([]); return; }
    let cancelled = false;
    const fetchData = async () => {
      if (!cancelled) setLoading(true);
      try {
        const data = await clicksAPI.getRecent(campaignId, 100);
        if (!cancelled) { setClicks(data); setLastUpdated(new Date()); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    setClicks([]);
    fetchData();
    const timer = setInterval(fetchData, REFRESH_MS);
    return () => { cancelled = true; clearInterval(timer); };
  }, [campaignId]);

  const scoreColor = s => s >= 70 ? 'var(--accent-red)' : s >= 40 ? 'var(--accent-orange)' : 'var(--accent-green)';

  if (!campaignId) {
    return (
      <div>
        <div className="page-header"><h1>Click Log</h1><p>Real-time click tracking</p></div>
        <div className="empty">
          <h3>No campaign selected</h3>
          <p>Select a campaign from the sidebar to see live click logs.</p>
          <NavLink to="/campaigns" className="btn btn-primary" style={{ marginTop: 12, display: 'inline-block' }}>
            Go to Campaigns →
          </NavLink>
        </div>
      </div>
    );
  }

  if (loading) return <div className="loading"><div className="spinner" /> Loading clicks...</div>;

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1>Click Log</h1>
          <p style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            Last 100 clicks
            {lastUpdated && (
              <span style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums' }}>
                · updated {lastUpdated.toLocaleTimeString()}
              </span>
            )}
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 11, color: 'var(--accent-green)', fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums' }}>● live</span>
          <span style={{ padding: '6px 12px', background: 'rgba(88,166,255,0.1)', border: '1px solid rgba(88,166,255,0.2)', borderRadius: 6, fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums', fontSize: 18, color: 'var(--accent-blue)', fontWeight: 700 }}>
            {clicks.length}
          </span>
        </div>
      </div>

      {clicks.length === 0 ? (
        <div className="empty">
          <h3>No clicks yet</h3>
          <p>Clicks will appear here as they are tracked on your campaign page.</p>
        </div>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Time</th>
                <th>IP Address</th>
                <th>Country</th>
                <th>Device</th>
                <th>Browser</th>
                <th>Score</th>
                <th>Signals</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {clicks.map((c, i) => (
                <tr key={c.id || i}>
                  <td style={{ color: 'var(--text-muted)', fontSize: 11, fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                    {new Date(c.created_at).toLocaleTimeString()}
                  </td>
                  <td><span className="ip-mono">{c.ip_address}</span></td>
                  <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{c.country || '—'}</td>
                  <td style={{ textTransform: 'capitalize', color: 'var(--text-secondary)', fontSize: 12 }}>{c.device_type}</td>
                  <td style={{ color: 'var(--text-secondary)', fontSize: 12 }}>{c.browser}</td>
                  <td>
                    <span style={{ fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums', fontSize: 13, color: scoreColor(c.fraud_score), fontWeight: 700 }}>
                      {c.fraud_score}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                      {(c.signals || []).slice(0, 2).map(s => (
                        <span key={s} className="badge warning" style={{ fontSize: 10 }}>
                          {SIGNAL_LABELS[s] || s.replace(/_/g, ' ')}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td>
                    <span className={`badge ${c.is_fraud ? 'fraud' : 'clean'}`}>
                      {c.is_fraud ? 'BLOCKED' : 'ALLOWED'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
